import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/config/brands';

interface MatrixLoaderProps {
  isLoading: boolean;
}

export const MatrixLoader = ({ isLoading }: MatrixLoaderProps) => {
  const brand = useBrand();
  const { t } = useTranslation(['common', 'map']);
  const isAhp = brand.design === 'civic';
  const [matrixChars, setMatrixChars] = useState<string[]>([]);

  useEffect(() => {
    const chars = '01ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?'.split('');
    setMatrixChars(chars);
  }, []);

  const generateMatrixRow = (width: number) => {
    return Array.from({ length: width }, () =>
      matrixChars[Math.floor(Math.random() * matrixChars.length)]
    );
  };

  if (!isLoading) return null;

  // --- AHP patriotic loader ---
  if (isAhp) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden" style={{ height: '100vh', background: 'linear-gradient(170deg, var(--civic-navy-deep) 0%, var(--civic-navy) 40%, var(--civic-navy-deeper) 100%)' }}>
        {/* Subtle star field */}
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${1 + Math.random() * 2}px`,
                height: `${1 + Math.random() * 2}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: 'var(--civic-cream)',
                opacity: 0.15 + Math.random() * 0.25,
                animation: `ahp-twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 3}s`,
              }}
            />
          ))}
        </div>

        {/* Slow sweeping gold light */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 600px 300px at 50% 50%, rgba(var(--civic-gold-rgb),0.06), transparent)',
            animation: 'ahp-glow-sweep 4s ease-in-out infinite',
          }}
        />

        {/* Central content */}
        <div className="relative z-10 text-center">
          {/* Shield / cross icon */}
          <div className="mx-auto mb-8 relative" style={{ width: 72, height: 72 }}>
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: '2px solid rgba(var(--civic-gold-rgb),0.4)',
                animation: 'ahp-ring-pulse 2s ease-in-out infinite',
              }}
            />
            {/* Inner glow */}
            <div
              className="absolute rounded-full"
              style={{
                inset: 6,
                background: 'radial-gradient(circle, rgba(var(--civic-gold-rgb),0.15) 0%, transparent 70%)',
              }}
            />
            {/* Cross icon */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div style={{ width: 28, height: 4, backgroundColor: 'var(--civic-gold)', borderRadius: 2, opacity: 0.9 }} />
              <div style={{ width: 4, height: 28, backgroundColor: 'var(--civic-gold)', borderRadius: 2, opacity: 0.9, position: 'absolute', top: -12, left: 12 }} />
            </div>
          </div>

          {/* Title */}
          <h3
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontSize: '1.5rem',
              color: 'var(--civic-cream)',
              letterSpacing: '-0.01em',
              lineHeight: 1.3,
              marginBottom: 6,
            }}
          >
            {t('common:brand')}
          </h3>

          {/* Subtitle */}
          <p
            style={{
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--civic-gold)',
              marginBottom: 28,
            }}
          >
            {t('map:loading')}
          </p>

          {/* Progress bar */}
          <div
            style={{
              width: 220,
              height: 3,
              backgroundColor: 'rgba(var(--civic-cream-rgb),0.08)',
              borderRadius: 2,
              margin: '0 auto',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: '35%',
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(90deg, transparent, var(--civic-gold), transparent)',
                animation: 'ahp-bar-slide 1.8s ease-in-out infinite',
              }}
            />
          </div>

          {/* Three dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: 'var(--civic-gold)',
                  animation: `ahp-dot-pulse 1.4s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Red accent stripe at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: 3,
            background: 'linear-gradient(90deg, transparent, var(--civic-red), transparent)',
            opacity: 0.6,
          }}
        />

        <style>{`
          @keyframes ahp-twinkle {
            0%, 100% { opacity: 0.15; }
            50% { opacity: 0.5; }
          }
          @keyframes ahp-glow-sweep {
            0%, 100% { opacity: 0.5; transform: translateY(0); }
            50% { opacity: 1; transform: translateY(-20px); }
          }
          @keyframes ahp-ring-pulse {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50% { transform: scale(1.08); opacity: 0.7; }
          }
          @keyframes ahp-bar-slide {
            0% { transform: translateX(-150%); }
            100% { transform: translateX(450%); }
          }
          @keyframes ahp-dot-pulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  // --- Default Matrix loader (Luigi's / AntiGreed) ---
  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center overflow-hidden" style={{ height: '100vh' }}>
      {/* Pixelated background pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #00ff41 50%, transparent 50%),
            linear-gradient(#00ff41 50%, transparent 50%)
          `,
          backgroundSize: '4px 4px',
          imageRendering: 'pixelated',
          filter: 'blur(0.5px)'
        }}
      />

      {/* Matrix rain effect */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="absolute text-green-400 opacity-60 font-mono text-xs leading-none whitespace-nowrap"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-20px`,
              imageRendering: 'pixelated',
              filter: 'contrast(1.2)',
              animation: `matrix-fall ${1 + Math.random() * 1}s linear infinite`,
              animationDelay: `${Math.random() * 2}s`,
              fontSize: `${8 + Math.random() * 6}px`
            }}
          >
            {generateMatrixRow(1 + Math.floor(Math.random() * 3)).join('')}
          </div>
        ))}
      </div>

      {/* Central loading content */}
      <div className="relative z-10 text-center">
        {/* Pixelated hospital icon */}
        <div className="mx-auto mb-6 w-16 h-16 relative">
          <div
            className="w-full h-full bg-green-400"
            style={{
              imageRendering: 'pixelated',
              clipPath: 'polygon(20% 0%, 80% 0%, 80% 20%, 100% 20%, 100% 80%, 80% 80%, 80% 100%, 20% 100%, 20% 80%, 0% 80%, 0% 20%, 20% 20%)',
              filter: 'drop-shadow(0 0 10px #00ff41)'
            }}
          />
          {/* Plus sign */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-2 bg-black"
            style={{ imageRendering: 'pixelated' }}
          />
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-8 bg-black"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>

        {/* Loading text with glitch effect */}
        <div className="space-y-2">
          <h3
            className="text-green-400 font-mono text-xl font-bold tracking-wider"
            style={{
              imageRendering: 'pixelated',
              textShadow: '0 0 10px #00ff41, 2px 0 0 #ff0040, -2px 0 0 #00ffff',
              animation: 'glitch 2s infinite'
            }}
          >
            {t('map:loading').toUpperCase()}
          </h3>

          <div className="flex items-center justify-center space-x-1">
            <span className="text-green-300 font-mono text-sm">{t('common:loading').toUpperCase()}</span>
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-green-400"
                  style={{
                    imageRendering: 'pixelated',
                    animation: `pulse-pixel ${1.5}s infinite`,
                    animationDelay: `${i * 0.3}s`
                  }}
                />
              ))}
            </div>
          </div>

          {/* Indeterminate progress bar */}
          <div className="w-64 h-3 bg-gray-800 border border-green-400 mx-auto mt-4 overflow-hidden" style={{ imageRendering: 'pixelated' }}>
            <div
              className="h-full w-1/3 bg-green-400"
              style={{
                imageRendering: 'pixelated',
                animation: 'loading-slide 1.5s ease-in-out infinite'
              }}
            />
          </div>
        </div>
      </div>

      {/* Global styles for animations */}
      <style>{`
        @keyframes matrix-fall {
          to {
            transform: translateY(100vh);
            opacity: 0;
          }
        }

        @keyframes glitch {
          0%, 100% { transform: translateX(0); }
          10% { transform: translateX(-2px); }
          20% { transform: translateX(2px); }
          30% { transform: translateX(-1px); }
          40% { transform: translateX(1px); }
          50% { transform: translateX(-2px); }
          60% { transform: translateX(2px); }
          70% { transform: translateX(-1px); }
          80% { transform: translateX(1px); }
          90% { transform: translateX(-2px); }
        }

        @keyframes pulse-pixel {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.8); }
        }

        @keyframes loading-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
};
